import React, { useCallback, useState } from 'react';
import { BaseLayout } from '../../components/Layout/Layout';
import { useBack } from '../../hooks/useBack';
import { useLanguage } from '../../locale/useLanguage';
import { PaymentMethod } from './components/PaymentMethod/PaymentMethod';

import postepayLogo from '../../assets/images/Postepay.png';
import postepayAcceptanceMark from '../../assets/images/postepay-am.svg';
import paypalImage from '../../assets/images/paypal.png';

import './PaymentMethodView.css';
import { VisaIcon } from '../../components/Icons/Visa';
import { MastercardIcon } from '../../components/Icons/Mastercard';
import { useCheckoutMutation } from '../../apis/mlkApi/mlk.api.hooks';
import { CheckoutProvider } from '../../models/payment.models';
import { parseRes } from '../../apis/utils';
import {
  useGetCmsConfigsQuery,
  useGetPrivacyPolicyQuery,
  useGetTermsAndConditionsQuery,
} from '../../apis/cmsApi/cms.hooks.api';
import { Routes } from '../../routes/types';
import { FaqFeature } from '../../models';
import { Link, useHistory } from 'react-router-dom';
import mlkLogo from '../../assets/images/mlk-fresh.png';

type PaymentMethodViewProps = {};

export const PaymentMethodView: React.FC<PaymentMethodViewProps> = () => {
  const goBack = useBack();
  const history = useHistory();

  const languageSettings = useLanguage();
  const t = languageSettings[0];
  const locale = languageSettings[2];

  const { data: termsAndConditions } = useGetTermsAndConditionsQuery({
    locale,
  });
  const { data: privacyPolicy } = useGetPrivacyPolicyQuery({
    locale,
  });

  const [isPrivacyAccepted, setPrivacyAccepted] = useState(false);
  const [isTermAccepted, setTermAccepted] = useState(false);
  const [isObligationsAccepted, setObligationsAccepted] = useState(false);
  const [checkoutCall] = useCheckoutMutation();
  const { data: cmsConfigs } = useGetCmsConfigsQuery(undefined);

  const isPaymentMethodsEnabled =
    isPrivacyAccepted && isTermAccepted && isObligationsAccepted;

  const onTermInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setTermAccepted(e.target.checked);
    },
    []
  );

  const onPrivacyInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPrivacyAccepted(e.target.checked);
    },
    []
  );

  const onObligationsInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setObligationsAccepted(e.target.checked);
    },
    []
  );

  const checkout = useCallback(
    (provider: CheckoutProvider) => {
      return () => {
        checkoutCall(provider).then((res) => {
          const { successRes, errorRes } = parseRes(res);
          if (successRes.data) {
            window.location.replace(successRes.data.redirectTo);
          }

          if (errorRes.error) {
            // TODO: Handle checkout error
          }
        });
      };
    },
    [checkoutCall]
  );

  const goToInfo = useCallback(() => {
    history.push(Routes.Faq.replace(':feature', FaqFeature.PaymentMethods));
  }, [history]);

  return (
    <BaseLayout
      title={t('pageTitles.paymentMethod')}
      hasBack
      hasInfo
      onInfoClick={goToInfo}
      onBackClick={goBack}
    >
      {cmsConfigs && (
        <div className="payment-methods--container">
          <h2>{t('paymentMethod.title')}</h2>
          <p>{t('paymentMethod.description')}</p>
          <div className="payment-methods--methods-container">
            <div className="payment-methods--checkbox-container">
              <input
                checked={isPrivacyAccepted}
                onChange={onPrivacyInputChange}
                type="checkbox"
                id="acceptPrivacy"
              />
              <label htmlFor="acceptPrivacy">
                {t('paymentMethod.privacyFirstDescription')}{' '}
                {privacyPolicy?.href && privacyPolicy?.target ? (
                  <a
                    className="link"
                    target={privacyPolicy?.target}
                    href={privacyPolicy?.href}
                  >
                    {t('paymentMethod.privacyLink')}
                  </a>
                ) : (
                  <Link to={Routes.PrivacyPolicy} className="link">
                    {t('paymentMethod.privacyLink')}
                  </Link>
                )}
                {t('paymentMethod.privacySecondDescription')}
              </label>
            </div>
            <div className="payment-methods--checkbox-container">
              <input
                checked={isTermAccepted}
                onChange={onTermInputChange}
                type="checkbox"
                id="acceptTerms"
              />
              <label htmlFor="acceptTerms">
                {t('paymentMethod.termsFirstDescription')}{' '}
                {termsAndConditions?.href && termsAndConditions?.target ? (
                  <a
                    className="link"
                    target={termsAndConditions.target}
                    href={termsAndConditions.href}
                  >
                    {t('paymentMethod.termsLink')}
                  </a>
                ) : (
                  <Link to={Routes.TermsAndConditions} className="link">
                    {t('paymentMethod.termsLink')}
                  </Link>
                )}
                {t('paymentMethod.termsSecondDescription')}
              </label>
            </div>
            <div className="payment-methods--checkbox-container">
              <input
                checked={isObligationsAccepted}
                onChange={onObligationsInputChange}
                type="checkbox"
                id="acceptObligations"
              />
              <label htmlFor="acceptObligations">
                {t('paymentMethod.obligationsAndResponsibilities')}
              </label>
            </div>
          </div>
          <div className="payment-methods--methods-container">
            {cmsConfigs.paymentMethods.creditCard && (
              <PaymentMethod
                onClick={
                  isPaymentMethodsEnabled
                    ? checkout(CheckoutProvider.Vpos)
                    : undefined
                }
                description={t('paymentMethod.paymentCard')}
                images={[
                  <VisaIcon />,
                  <MastercardIcon height={20} />,
                  <img
                    src={postepayAcceptanceMark}
                    height={26}
                    alt="PostePay logo"
                  />,
                ]}
              />
            )}
            {cmsConfigs.paymentMethods.postePay && (
              <PaymentMethod
                onClick={
                  isPaymentMethodsEnabled
                    ? checkout(CheckoutProvider.Vpos)
                    : undefined
                }
                isPostePay
                images={[
                  <img src={postepayLogo} height={18} alt="PostePay logo" />,
                ]}
              />
            )}
            {cmsConfigs.paymentMethods.payPal && (
              <PaymentMethod
                onClick={
                  isPaymentMethodsEnabled
                    ? checkout(CheckoutProvider.PayPal)
                    : undefined
                }
                images={[
                  <img src={paypalImage} height={18} alt="Paypal logo" />,
                ]}
              />
            )}
          </div>
          <div>
            <img height="100px" src={mlkLogo} alt="logo mlk" />
            <p>{t('paymentMethod.handledByMlk')}</p>
          </div>
        </div>
      )}
    </BaseLayout>
  );
};
